import React, { useState } from 'react';
import { ExclamationTriangleIcon, TrashIcon, UserIcon, BuildingOffice2Icon } from '@heroicons/react/24/outline';
import settingsService from '../../services/settingsService';
import authService from '../../services/auth';
import { useNavigate } from 'react-router-dom';

const DangerZoneTab = ({ onNotify }) => {
  const navigate = useNavigate();
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [showDeleteCompany, setShowDeleteCompany] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    if (confirmText !== 'DELETE') {
      onNotify('Type DELETE to confirm', 'error');
      return;
    }
    setDeleting(true);
    try {
      await settingsService.deleteAccount(password);
      authService.logout();
      onNotify('Account deleted permanently');
      navigate('/login');
    } catch (err) {
      onNotify(err.response?.data?.detail || 'Failed to delete account', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCompany = async (e) => {
    e.preventDefault();
    if (confirmText !== 'DELETE') {
      onNotify('Type DELETE to confirm', 'error');
      return;
    }
    setDeleting(true);
    try {
      await settingsService.deleteCompanyData(password);
      authService.logout();
      onNotify('Company data deleted permanently');
      navigate('/login');
    } catch (err) {
      onNotify(err.response?.data?.detail || 'Failed to delete company data', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const resetForm = () => {
    setPassword('');
    setConfirmText('');
  };

  const toggleDeleteAccount = () => {
    setShowDeleteAccount(!showDeleteAccount);
    setShowDeleteCompany(false);
    resetForm();
  };

  const toggleDeleteCompany = () => {
    setShowDeleteCompany(!showDeleteCompany);
    setShowDeleteAccount(false);
    resetForm();
  };

  return (
    <div className="space-y-6">
      {/* Warning Banner */}
      <div className="bg-red-900/20 border border-red-800 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <ExclamationTriangleIcon className="h-6 w-6 text-red-400 flex-shrink-0" />
          <div>
            <p className="text-sm text-red-300 font-medium">Danger Zone</p>
            <p className="text-xs text-red-400/70 mt-0.5">
              These actions are irreversible. Please proceed with caution.
            </p>
          </div>
        </div>
      </div>

      {/* Delete Account */}
      <div className="bg-gray-800 border border-red-900/50 rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <UserIcon className="h-6 w-6 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-medium text-white">Delete Account</h3>
              <p className="text-sm text-gray-400 mt-1">
                Permanently delete your user account, tokens, and memberships. This cannot be undone.
              </p>
            </div>
          </div>
          <button
            onClick={toggleDeleteAccount}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm transition-colors flex-shrink-0"
          >
            Delete Account
          </button>
        </div>

        {showDeleteAccount && (
          <form onSubmit={handleDeleteAccount} className="mt-4 pt-4 border-t border-gray-700 space-y-4">
            <div>
              <label htmlFor="delete-account-password" className="block text-sm font-medium text-gray-300 mb-2">Enter your password</label>
              <input
                id="delete-account-password"
                name="delete-account-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-red-500"
                placeholder="Your password"
              />
            </div>
            <div>
              <label htmlFor="delete-account-confirm" className="block text-sm font-medium text-gray-300 mb-2">
                Type <span className="text-red-400 font-mono">DELETE</span> to confirm
              </label>
              <input
                id="delete-account-confirm"
                name="delete-account-confirm"
                type="text"
                autoComplete="off"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                required
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-red-500"
                placeholder="DELETE"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors motion-reduce:transition-none"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg transition-colors motion-reduce:transition-none"
              >
                {deleting ? 'Deleting…' : 'Permanently Delete'}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Delete Company Data */}
      <div className="bg-gray-800 border border-red-900/50 rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <BuildingOffice2Icon className="h-6 w-6 text-red-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <h3 className="text-lg font-medium text-white">Delete Company Data</h3>
              <p className="text-sm text-gray-400 mt-1">
                Permanently delete the company, all team memberships, and associated data. Only company owners can perform this action.
              </p>
            </div>
          </div>
          <button
            onClick={toggleDeleteCompany}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm transition-colors flex-shrink-0"
          >
            Delete Company
          </button>
        </div>

        {showDeleteCompany && (
          <form onSubmit={handleDeleteCompany} className="mt-4 pt-4 border-t border-gray-700 space-y-4">
            <div>
              <label htmlFor="delete-company-password" className="block text-sm font-medium text-gray-300 mb-2">Enter your password</label>
              <input
                id="delete-company-password"
                name="delete-company-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-red-500"
                placeholder="Your password"
              />
            </div>
            <div>
              <label htmlFor="delete-company-confirm" className="block text-sm font-medium text-gray-300 mb-2">
                Type <span className="text-red-400 font-mono">DELETE</span> to confirm
              </label>
              <input
                id="delete-company-confirm"
                name="delete-company-confirm"
                type="text"
                autoComplete="off"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                required
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-red-500"
                placeholder="DELETE"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg transition-colors"
              >
                {deleting ? 'Deleting…' : 'Permanently Delete'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default DangerZoneTab;
